// Bookingen i en ramme på klientens/brandets hjemmeside (public/widget.js – docs/INDLEJRING.md).
// "side": bookingen står direkte på siden – gennemsigtig baggrund, ingen login, og højden sendes til siden, så rammen
// vokser og krymper med indholdet. "vindue": åbnet i et lag over siden (kryds og Esc lukker det).
import './d/indlejring.css';
import { godkendteVaerter, indlejringParam, vaertGodkendt, type IndlejringData } from '../core/indlejring';
import { bland, paa, type KlientBrand } from '../core/klient';
import { anvendTema, lukIndlejret, rulIndlejret, saetIndlejret } from './lib';

/** Indlejret? Læser ?indlejret=1 eller #/…?indlejret=1 (widget.js). Kun når siden faktisk står i en ramme. */
export function startIndlejret(hash: string) {
  let iRamme = false;
  try { iRamme = window.self !== window.top; } catch { iRamme = true; }
  const q = new URLSearchParams(location.search), h = new URLSearchParams(hash.split('?')[1] ?? '');
  const p = indlejringParam(q.get('indlejret') ? q : h);
  if (!p.indlejret || !iRamme) return;
  const luk = p.luk || h.get('luk') === '1';
  const tilstand = luk ? 'vindue' : 'side';
  saetIndlejret(tilstand);
  const rod = document.documentElement;
  rod.dataset.indlejret = tilstand;
  // Hjemmesidens farver kendes ikke: lyst tema, medmindre siden beder om mørkt (data-tema="moerk")
  anvendTema((q.get('tema') ?? h.get('tema')) === 'moerk' ? 'moerk' : 'lys');
  if (luk) {
    // Esc lukker vinduet på hjemmesiden – medmindre et vindue i bookingen er åbent
    addEventListener('keydown', (e) => { if (e.key === 'Escape' && !document.querySelector('.modal, [role="dialog"]')) lukIndlejret(); });
    return;
  }
  // Links ud af bookingen (betingelser, privatlivspolitik, Trustpilot, kalender) åbner i en ny fane – ikke i rammen
  document.addEventListener('click', (e) => {
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (a && /^https?:/.test(a.href) && new URL(a.href).origin !== location.origin) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }
  }, true);
  let sidst = 0;
  const send = () => {
    const h2 = hoejde();
    if (Math.abs(h2 - sidst) < 2) return;
    sidst = h2;
    try { window.parent.postMessage({ freemi: 'hoejde', h: h2 }, '*'); } catch { /* ok */ }
  };
  const ro = new ResizeObserver(send);
  ro.observe(document.body);
  new MutationObserver(() => { const r = document.getElementById('rod'); if (r) ro.observe(r); send(); }).observe(document.body, { childList: true, subtree: true });
  addEventListener('load', send);
  addEventListener('hashchange', () => { rulIndlejret(); requestAnimationFrame(send); });
}

/** Indholdets højde: siden selv + vinduer i bookingen (de ligger fast og tæller ellers ikke med). */
function hoejde(): number {
  const rod = document.getElementById('rod');
  let h = rod ? rod.getBoundingClientRect().height : document.body.scrollHeight;
  for (const m of document.querySelectorAll<HTMLElement>('.modal, [role="dialog"]')) h = Math.max(h, m.scrollHeight + 48);
  return Math.ceil(h);
}

/** Siderne, der viser bookingen: er de godkendt? null = kan ikke ses (ingen Referer/ancestorOrigins) – så afgør CSP'en alene. */
export function foraeldreGodkendt(d: IndlejringData, brand: string | null): boolean | null {
  let fra: string[] = [];
  try {
    const ao = (location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
    fra = ao?.length ? [...ao] : document.referrer ? [new URL(document.referrer).origin] : [];
  } catch { /* ukendt */ }
  // Udvikling på egen maskine (localhost) afgøres ikke her – i drift afviser CSP'en den alligevel
  fra = fra.filter((x) => x && x !== 'null' && !/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(x));
  if (!fra.length) return null;
  const vaerter = godkendteVaerter(d, brand);
  // Alle sider uden om bookingen skal være godkendt (som CSP frame-ancestors) – Freemi selv er altid ok (fx forhåndsvisningen)
  return fra.every((x) => x === location.origin || vaertGodkendt(x, vaerter));
}

/** data-farve="brand": brandets hovedfarve på knapper og valg (ellers Freemis fælles design). */
export function anvendIndlejretFarve(design: KlientBrand | null) {
  const s = document.documentElement.style;
  const q = new URLSearchParams(location.search);
  if (!design || q.get('farve') !== 'brand' || !/^#[0-9a-f]{6}$/i.test(design.primaer)) {
    for (const k of ['--primaer', '--primaer-tekst', '--fokus', '--lime-svag']) s.removeProperty(k);
    return;
  }
  s.setProperty('--primaer', design.primaer);
  s.setProperty('--primaer-tekst', paa(design.primaer, '#15202B'));
  s.setProperty('--fokus', design.primaer);
  s.setProperty('--lime-svag', bland(design.primaer, '#FFFFFF', 0.9));
}
